import * as styles from "./Checkbox.css";

import { createDrawable, createTimeline } from "animejs";
import type { DrawableSVGGeometry, Timeline } from "animejs";
import { clsx } from "clsx";
import type { JSX, RefObject } from "react";
import { useLayoutEffect, useRef, useState } from "react";

import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { Icon } from "@frontend/components/atoms/Icon";
import { Ripple } from "@frontend/components/atoms/Ripple";
import { TRANSITION_FAST_MS, TRANSITION_SLOW_MS } from "@frontend/styles";

import type { CheckboxProps } from "./Checkbox.types";
import { CHECKBOX_RIPPLE_SPREAD } from "./constants";

function useCheckAnimation(
  fillRef: RefObject<HTMLSpanElement | null>,
  svgRef: RefObject<SVGSVGElement | null>,
  visible: boolean,
) {
  const drawableRef = useRef<DrawableSVGGeometry | null>(null);
  const timelineRef = useRef<Timeline | null>(null);

  useLayoutEffect(() => {
    const fillEl = fillRef.current;
    const path = svgRef.current?.querySelector("path");

    if (!fillEl || !path) {
      return;
    }

    if (!drawableRef.current) {
      [drawableRef.current] = createDrawable(path, visible ? 0 : 1, 1);
      fillEl.style.opacity = visible ? "1" : "0";

      return;
    }

    timelineRef.current?.cancel();

    const fill = {
      opacity: visible ? 1 : 0,
      duration: TRANSITION_SLOW_MS,
    };

    const draw = {
      draw: visible ? "0 1" : "1 1",
      duration: TRANSITION_FAST_MS,
    };

    const timeline = createTimeline({ defaults: { ease: "outQuad" } });

    if (visible) {
      timeline.add(fillEl, fill).add(drawableRef.current, draw);
    } else {
      timeline.add(drawableRef.current, draw).add(fillEl, fill);
    }

    timelineRef.current = timeline;
  }, [fillRef, svgRef, visible]);
}

export function Checkbox({
  size,
  label,
  className,
  onCheckedChange,
  ...props
}: CheckboxProps): JSX.Element {
  const isControlled = props.checked !== undefined;
  const [uncontrolledChecked, setUncontrolledChecked] = useState(
    () => props.defaultChecked ?? false,
  );
  const checked = isControlled ? Boolean(props.checked) : uncontrolledChecked;
  const visible = checked || (props.indeterminate ?? false);

  const fillRef = useRef<HTMLSpanElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useCheckAnimation(fillRef, svgRef, visible);

  function handleCheckedChange(
    next: boolean,
    eventDetails: BaseCheckbox.Root.ChangeEventDetails,
  ) {
    if (!isControlled) {
      setUncontrolledChecked(next);
    }

    onCheckedChange?.(next, eventDetails);
  }

  return (
    <label className={styles.label}>
      <BaseCheckbox.Root
        data-slot="checkbox"
        className={clsx(styles.checkbox({ size }), className)}
        {...props}
        onCheckedChange={handleCheckedChange}
      >
        <Ripple shape="circle" spread={CHECKBOX_RIPPLE_SPREAD} center />
        <span ref={fillRef} aria-hidden className={styles.fill} />
        <BaseCheckbox.Indicator keepMounted className={styles.indicator}>
          <Icon
            ref={svgRef}
            name="Check"
            size={size}
            strokeWidth="2"
            absoluteStrokeWidth={true}
          />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      <span className={styles.labelText}>{label}</span>
    </label>
  );
}
